import { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { toast } from 'react-toastify';
import { storyCardApi, StoryCardTemplate } from '../../../../services';
import {
  Button,
  ButtonType,
  ConfirmationModal,
  getCssVar,
  IconLabel,
  STORY_CARD_TEMPLATES_LIMIT,
  useIsNarrowViewport,
} from '../../../../shared';
import { CardTemplateModal } from './CardTemplateModal';

import classNames from 'classnames';
import categoryClasses from '../Category/Category.module.scss';

export const CardTemplates: FunctionComponent = () => {
  const { t } = useTranslation();

  const { t: ctxT } = useTranslation('', { keyPrefix: 'storyCardTemplates' });

  const [storyCardTemplates] = storyCardApi.endpoints.storyCardTemplates.useLazyQuery();

  const [storyCardTemplateDelete] = storyCardApi.endpoints.storyCardTemplateDelete.useMutation();

  const isNarrowViewport = useIsNarrowViewport();

  const [isFetchingTemplates, setIsFetchingTemplates] = useState<boolean>(true);

  const [templates, setTemplates] = useState<StoryCardTemplate[]>([]);

  const [isCardTemplateModalOpen, setIsCardTemplateModalOpen] = useState<boolean>(false);

  const [editTemplate, setEditTemplate] = useState<StoryCardTemplate | undefined>();

  const [deleteTemplateId, setDeleteTemplateId] = useState<number | null>(null);

  const getTemplates = useCallback(async () => {
    setIsFetchingTemplates(true);

    try {
      setTemplates(await storyCardTemplates().unwrap());
    } catch (_) {
      toast.error(t('common.error-message'));
    } finally {
      setIsFetchingTemplates(false);
    }
  }, [storyCardTemplates, t]);

  useEffect(() => {
    getTemplates();
  }, [getTemplates]);

  const editHandler = useCallback((template: StoryCardTemplate) => {
    setEditTemplate(template);
    setIsCardTemplateModalOpen(true);
  }, []);

  const cardTemplateModalCloseHandler = useCallback(() => {
    setIsCardTemplateModalOpen(false);
    setEditTemplate(undefined);
  }, []);

  const deleteHandler = useCallback(async () => {
    if (!deleteTemplateId) {
      return;
    }

    try {
      const { payload } = await storyCardTemplateDelete({ storyCardId: deleteTemplateId }).unwrap();

      if (!payload) {
        toast.error(t('common.error-message'));
      }
    } catch (_) {
      toast.error(t('common.error-message'));
    }

    setDeleteTemplateId(null);

    getTemplates();
  }, [deleteTemplateId, getTemplates, storyCardTemplateDelete, t]);

  const content = useMemo(() => {
    if (isFetchingTemplates) {
      return <Skeleton height={'5rem'} />;
    }

    if (!templates.length) {
      return (
        <div className={categoryClasses['category__list-wrapper']}>
          <div className={categoryClasses['category__no-content']}>{ctxT('no-content')}</div>
        </div>
      );
    }

    return (
      <div className={categoryClasses['category__list-wrapper']}>
        <table
          className={classNames(categoryClasses['category__list'], {
            [categoryClasses['category__list--narrow']]: isNarrowViewport,
          })}
        >
          <thead>
            <tr>
              <th>{t('storyCardCategoryList.title')}</th>
              <th>{t('common.type')}</th>
              <th>{t('storyCardCategory.show-as-option')}</th>
              <th>{t('storyCardCategory.show-as-button')}</th>
              <th>{t('storyCardCategoryList.assigned-to-story')}</th>
              <th>{t('storyCardCategoryList.channel')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {templates.map((template) => {
              const { id, name, settings, storyCard, story, channel } = template;

              return (
                <tr key={id}>
                  <td>
                    <button
                      type={'button'}
                      className={categoryClasses['category__name']}
                      title={name}
                      onClick={() => editHandler(template)}
                    >
                      {name}
                    </button>
                  </td>
                  <td>{t(`cardTypes.${storyCard.type.toLowerCase()}`)}</td>
                  <td>{t(`common.${settings.showAsOption ? 'yes' : 'no'}`)}</td>
                  <td>{t(`common.${settings.showAsButton ? 'yes' : 'no'}`)}</td>
                  <td>
                    <div className={categoryClasses['category__story']} title={story?.title}>
                      {story?.title}
                    </div>
                  </td>
                  <td>{channel?.name}</td>
                  <td>
                    <div className={categoryClasses['category__actions']}>
                      <IconLabel
                        iconId={'edit'}
                        iconSize={18}
                        ariaLabel={t('common.edit')}
                        color={getCssVar('--base-link-text-color')}
                        hoverColor={getCssVar('--base-link-text-hover-color')}
                        onClick={() => editHandler(template)}
                      />
                      <IconLabel
                        iconId={'delete'}
                        iconSize={20}
                        ariaLabel={t('common.delete')}
                        color={getCssVar('--color-danger')}
                        hoverColor={getCssVar('--color-danger-hover')}
                        onClick={() => setDeleteTemplateId(id)}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }, [ctxT, editHandler, isFetchingTemplates, isNarrowViewport, t, templates]);

  return (
    <>
      <div className={categoryClasses['category']}>
        <div className={categoryClasses['category__header']}>
          <h2 className={categoryClasses['category__header-title']}>{ctxT('title')}</h2>
          <Button
            type={ButtonType.primary}
            label={ctxT('new-template')}
            onClick={() => setIsCardTemplateModalOpen(true)}
            disabled={isFetchingTemplates || templates.length >= STORY_CARD_TEMPLATES_LIMIT}
          />
        </div>
        {content}
      </div>

      {isCardTemplateModalOpen && (
        <CardTemplateModal
          isOpen={isCardTemplateModalOpen}
          onClose={cardTemplateModalCloseHandler}
          submitCallback={getTemplates}
          template={editTemplate}
        />
      )}

      <ConfirmationModal
        isOpen={Boolean(deleteTemplateId)}
        onClose={() => setDeleteTemplateId(null)}
        acceptLabel={t('common.delete')}
        onAccept={deleteHandler}
        title={t('storyCardCategoryTemplate.delete-confirmation-title')}
        subTitle={t('storyCardCategoryTemplate.delete-confirmation-sub-title')}
        danger
      />
    </>
  );
};
